import { apiClient } from './apiClient';
import { mockSignatureRequests } from '@/mocks/data';
import type { SignatureRequest } from '@/types';

const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true' || !import.meta.env.VITE_API_BASE_URL;

export interface SignatureRect {
  page: number;       // 1-indexed, 0 = trang cuối
  xRatio: number;     // 0-1 offset trái
  yRatio: number;     // 0-1 offset top-down
  wRatio: number;     // 0-1
  hRatio: number;     // 0-1
}

export const signingService = {
  async list(filter: 'all' | 'pending' | 'my-turn' | 'sent-by-me' | 'completed' | 'rejected' = 'all'): Promise<SignatureRequest[]> {
    if (filter === 'pending') return this.getPending();
    const { data } = await apiClient.get<SignatureRequest[]>('/signatures', { params: { filter } });
    return data;
  },

  async quickAttachment(file: File): Promise<{ id: string; fileName: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await apiClient.post('/signatures/quick-attachment', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  async create(payload: {
    attachmentId: string;
    signerIds: string[];
    workflow?: 'SEQUENTIAL' | 'PARALLEL';
    taskId?: string;
    title?: string;
    taskDescription?: string;
    categoryId?: string;
    dueDate?: string;
    /** Legacy: 1 user 1 vị trí (giữ để compat) */
    signerPositions?: Record<string, SignatureRect>;
    /** V1.0.63c: 1 user nhiều vị trí trên cùng file - cho phép trùng signerId */
    signerPositionList?: Array<{ signerId: string; rect: SignatureRect }>;
    /** V1.0.63i: ID các attachment đính kèm thêm (tài liệu liên quan) */
    relatedAttachmentIds?: string[];
  }): Promise<SignatureRequest> {
    // Filter empty strings
    const clean = {
      ...payload,
      signerIds: payload.signerIds.filter(id => id && id !== ''),
    };
    const { data } = await apiClient.post<SignatureRequest>('/signatures', clean);
    return data;
  },

  async getOriginalPdf(id: string): Promise<Blob> {
    const { data } = await apiClient.get(`/signatures/${id}/original-pdf`, { responseType: 'blob' });
    return data;
  },

  async getSignedPdf(id: string): Promise<Blob> {
    const { data } = await apiClient.get(`/signatures/${id}/signed-pdf`, { responseType: 'blob' });
    return data;
  },

  async setPosition(id: string, signerId: string, rect: SignatureRect): Promise<void> {
    await apiClient.post(`/signatures/${id}/positions`, { signerId, rect });
  },

  /** V1.0.63i: Sửa thông tin yêu cầu ký - gắn task, tiêu đề, loại VB, hạn ký */
  async updateInfo(id: string, info: {
    taskId?: string | null;
    title?: string;
    description?: string;
    categoryId?: string | null;
    dueDate?: string | null;
    confidentiality?: string | null;
  }): Promise<void> {
    await apiClient.patch(`/signatures/${id}/info`, info);
  },

  /** V1.0.66: Đính kèm thêm tài liệu tham chiếu vào phiếu ký đã tạo */
  async addRelatedAttachments(id: string, attachmentIds: string[]): Promise<void> {
    await apiClient.post(`/signatures/${id}/related-attachments`, { attachmentIds });
  },

  /** V1.0.66: Gỡ 1 tài liệu tham chiếu khỏi phiếu ký */
  async removeRelatedAttachment(id: string, attachmentId: string): Promise<void> {
    await apiClient.delete(`/signatures/${id}/related-attachments/${attachmentId}`);
  },

  /** V1.0.63i: Số đếm cho các tab. V1.0.65: thêm archived */
  async getCounts(): Promise<{ pending: number; sentByMe: number; completed: number; rejected: number; archived: number }> {
    const { data } = await apiClient.get('/signatures/counts');
    return data;
  },

  /** V1.0.65: Đưa phiếu ký vào lưu trữ (chỉ creator, phiếu phải SIGNED hoặc REJECTED) */
  async archive(signatureRequestId: string): Promise<void> {
    await apiClient.post(`/signatures/${signatureRequestId}/archive`);
  },

  /** V1.0.65: Khôi phục phiếu khỏi lưu trữ */
  async unarchive(signatureRequestId: string): Promise<void> {
    await apiClient.post(`/signatures/${signatureRequestId}/unarchive`);
  },

  async getPending(): Promise<SignatureRequest[]> {
    if (USE_MOCK) {
      await new Promise((r) => setTimeout(r, 300));
      return mockSignatureRequests;
    }
    const { data } = await apiClient.get<SignatureRequest[]>('/signatures/pending');
    return data;
  },

  async getById(id: string): Promise<SignatureRequest> {
    if (USE_MOCK) {
      const sig = mockSignatureRequests.find((s) => s.id === id);
      if (!sig) throw new Error('Signature request không tồn tại');
      await new Promise((r) => setTimeout(r, 200));
      return sig;
    }
    const { data } = await apiClient.get<SignatureRequest>(`/signatures/${id}`);
    return data;
  },

  async requestOtp(signatureRequestId: string, channel: 'EMAIL' | 'SMS' = 'EMAIL'): Promise<{
    expiresIn: number;
    maskedDestination: string;
  }> {
    if (USE_MOCK) {
      await new Promise((r) => setTimeout(r, 400));
      return { expiresIn: 300, maskedDestination: 'ngu***@hungviet.vn' };
    }
    const { data } = await apiClient.post<{ expiresIn: number; maskedDestination: string }>(
      `/signatures/${signatureRequestId}/otp/request`,
      { purpose: 'SIGN', contextId: signatureRequestId, channel },
    );
    return data;
  },

  async sign(
    signatureRequestId: string,
    otpCode: string,
  ): Promise<{ success: boolean; sigCode: string }> {
    if (USE_MOCK) {
      await new Promise((r) => setTimeout(r, 800));
      if (otpCode.length !== 6) {
        throw new Error('Mã OTP không hợp lệ');
      }
      return {
        success: true,
        sigCode: `SIG-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${Math.random()
          .toString(36)
          .slice(2, 8)
          .toUpperCase()}`,
      };
    }
    const { data } = await apiClient.post<{ success: boolean; sigCode: string }>(
      `/signatures/${signatureRequestId}/sign`,
      { otpCode },
    );
    return data;
  },

  async reject(signatureRequestId: string, reason: string): Promise<void> {
    await apiClient.post(`/signatures/${signatureRequestId}/reject`, { reason });
  },

  async delete(signatureRequestId: string): Promise<void> {
    await apiClient.delete(`/signatures/${signatureRequestId}`);
  },

  // V1.0.40: Lịch sử + Bình luận
  async getEvents(signatureRequestId: string): Promise<SigningEvent[]> {
    const { data } = await apiClient.get<SigningEvent[]>(`/signatures/${signatureRequestId}/events`);
    return data;
  },

  async addComment(signatureRequestId: string, content: string): Promise<SigningEvent> {
    const { data } = await apiClient.post<SigningEvent>(
      `/signatures/${signatureRequestId}/comments`,
      { content },
    );
    return data;
  },

  // V1.0.63e: Quản lý signers - cần permission signing:manage_signers hoặc là creator
  async removeSigner(signatureRequestId: string, signerId: string, reason?: string): Promise<void> {
    const params = reason ? `?reason=${encodeURIComponent(reason)}` : '';
    await apiClient.delete(`/signatures/${signatureRequestId}/signers/${signerId}${params}`);
  },

  async replaceSigner(
    signatureRequestId: string,
    oldSignerId: string,
    newSignerId: string,
    reason?: string,
  ): Promise<void> {
    await apiClient.post(
      `/signatures/${signatureRequestId}/signers/${oldSignerId}/replace`,
      { newSignerId, reason },
    );
  },
};

// V1.0.40: Types cho lịch sử + bình luận
export interface SigningEvent {
  id: string;
  requestId: string;
  /** CREATE_REQUEST | OTP_REQUEST | SIGN | REJECT | COMMENT | DELETE | UPDATE_POSITION */
  action: string;
  actorId?: string | null;
  actorName?: string | null;
  actorAvatar?: string | null;
  content?: string | null;
  targetUserId?: string | null;
  targetUserName?: string | null;
  createdAt: string;
}
